// ===========================================
// HOOK: useCrearEventoForm
// ===========================================
// TanStack Form hook for creating/editing calendar events

import { useForm } from '@tanstack/react-form'
import { crearEventoSchema, type CrearEventoFormValues } from './crearEventoSchema'

interface UseCrearEventoFormOptions {
  onSubmit?: (value: CrearEventoFormValues) => Promise<void>
  defaultValues?: Partial<CrearEventoFormValues>
}

/**
 * ORR-295: el alta del calendario crea reuniones con Meet, y este es el tipo que lo dispara.
 *
 * `useCalendario` corta la llamada a Google con `tipo !== 'reunion'`. Mientras el default de
 * este formulario fue `'otro'`, quien no tocaba el selector guardaba un evento mudo: sin sala,
 * sin invitaciones y sin nada en la agenda del estudio — todo sin un solo error a la vista.
 * Por eso el valor vive en una constante con un test que lo fija, y no suelto acá abajo.
 */
export const TIPO_EVENTO_POR_DEFECTO = 'reunion'

export const VALORES_INICIALES = {
  titulo: '',
  descripcion: null,
  tipo: TIPO_EVENTO_POR_DEFECTO,
  fecha_inicio: '',
  hora_inicio: null,
  fecha_fin: null,
  hora_fin: null,
  todo_el_dia: true,
  estado: 'pendiente',
  color: null,
  ubicacion: null,
  notas: null,
  participantes: [],
  // ORR-383: el default es no repetir. Una serie creada sin querer manda invitaciones
  // recurrentes a gente real, así que la repetición se elige, nunca se hereda.
  repeticion: 'ninguna',
  repetir_hasta: null,
} as CrearEventoFormValues

export function useCrearEventoForm(options: UseCrearEventoFormOptions = {}) {
  const form = useForm({
    defaultValues: {
      ...VALORES_INICIALES,
      ...options.defaultValues,
    } as CrearEventoFormValues,
    validators: {
      onSubmit: crearEventoSchema,
    },
    onSubmit: async ({ value }) => {
      if (options.onSubmit) {
        await options.onSubmit(value)
      }
    },
  })

  return { form }
}

export type UseCrearEventoFormReturn = ReturnType<typeof useCrearEventoForm>
export type CrearEventoForm = UseCrearEventoFormReturn['form']

/**
 * Arma el rango del evento a partir de los valores del formulario (ORR-301).
 *
 * Vivía adentro del `onSubmit` del diálogo. Se extrajo porque el chequeo de conflictos
 * necesita el MISMO rango que se va a guardar: recalcularlo aparte abría la puerta a que
 * se consultara una ventana y se agendara otra.
 *
 * ORR-295: sin fecha de fin, el fin cae al cierre del MISMO día en vez de quedar null.
 * `calendarioService` manda `fecha_fin ?? fecha_inicio` a Google, así que un null dejaba
 * start == end — una reunión de duración cero.
 */
export const armarRangoEvento = (
  value: CrearEventoFormValues,
): { fechaInicio: string; fechaFin: string | null } => {
  const fechaInicio =
    value.todo_el_dia || !value.hora_inicio
      ? `${value.fecha_inicio}T00:00:00`
      : `${value.fecha_inicio}T${value.hora_inicio}:00`

  const fechaFinBase = value.fecha_fin ?? value.fecha_inicio
  const fechaFin = fechaFinBase
    ? value.todo_el_dia || !value.hora_fin
      ? `${fechaFinBase}T23:59:59`
      : `${fechaFinBase}T${value.hora_fin}:00`
    : null

  return { fechaInicio, fechaFin }
}

/**
 * Un evento de día completo choca contra todo lo que haya ese día, así que consultar la
 * agenda no aportaría nada: el aviso saltaría siempre y se volvería ruido que se ignora.
 */
export const tieneHorarioConsultable = (value: CrearEventoFormValues): boolean =>
  !value.todo_el_dia && Boolean(value.hora_inicio)

/**
 * ORR-389: minutos que se suman a la hora de inicio para sugerir la hora de fin.
 */
export const OFFSET_HORA_FIN_MINUTOS = 30

/**
 * Suma minutos a una hora `HH:mm` y señala si el resultado cruza la medianoche.
 *
 * ORR-389: 23:45 + 30 minutos da 00:15, pero de OTRO día. El caller decide qué hacer con
 * `cruzaMedianoche` — acá, correr la fecha de fin un día — en vez de dejarlo indefinido.
 */
export const sumarMinutosAHora = (
  hora: string,
  minutos: number,
): { hora: string; cruzaMedianoche: boolean } => {
  const [h, m] = hora.split(':').map(Number)
  const totalMinutos = h * 60 + m + minutos
  const normalizado = ((totalMinutos % (24 * 60)) + 24 * 60) % (24 * 60)
  const horaResultado = String(Math.floor(normalizado / 60)).padStart(2, '0')
  const minutoResultado = String(normalizado % 60).padStart(2, '0')
  return {
    hora: `${horaResultado}:${minutoResultado}`,
    cruzaMedianoche: totalMinutos >= 24 * 60,
  }
}

/**
 * Fecha de fin sugerida al cargar la fecha de inicio (ORR-389: autocompletar en pantalla,
 * editable). Devuelve `null` cuando NO corresponde tocar el campo — porque el usuario ya lo
 * editó a mano y pisarlo molesta más de lo que ayuda.
 */
export const fechaFinSugerida = (
  fechaInicio: string,
  fechaFinFueEditadaAMano: boolean,
): string | null => (fechaFinFueEditadaAMano ? null : fechaInicio)

/**
 * Hora de fin sugerida al cargar la hora de inicio: inicio + `OFFSET_HORA_FIN_MINUTOS`.
 * `null` cuando el usuario ya editó la hora de fin a mano — mismo criterio que
 * `fechaFinSugerida`. Si la sugerencia cruza la medianoche, el caller es quien corre la
 * fecha de fin un día (acá solo se calcula la hora).
 */
export const horaFinSugerida = (
  horaInicio: string,
  horaFinFueEditadaAMano: boolean,
): { hora: string; cruzaMedianoche: boolean } | null =>
  horaFinFueEditadaAMano ? null : sumarMinutosAHora(horaInicio, OFFSET_HORA_FIN_MINUTOS)
